import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from '@emotion/styled'
import { Radio, DatePicker, Checkbox, Button, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { createSimpleStore } from 'react-simple-reducer'
import dayjs from 'dayjs'
import Axios from 'axios'
import * as d3 from 'd3'
import { AUTOFILTERS, BiCourseInfo, CursoMetric } from './BiDesempenhoCursos'

const { RangePicker } = DatePicker

interface BiEstadoPoint {
  uf: string
  cursoId: number
  count: number
  valorNewDriverSum: number
}

interface UfRow {
  uf: string
  total: number
  share: number
}

type Tipo = 'todos' | 'venda_direta' | 'parceiros'

const PIE_SIZE = 380
const OUTROS_LABEL = 'Outros'
const MIN_SLICE_SHARE = 0.02
const MIN_LABEL_SHARE = 0.04

const UF_COLORS: Record<string, string> = {
  AC: '#3b82f6', AL: '#f59e0b', AM: '#10b981', AP: '#ef4444', BA: '#8b5cf6', CE: '#06b6d4',
  DF: '#f97316', ES: '#84cc16', GO: '#ec4899', MA: '#6366f1', MG: '#14b8a6', MS: '#eab308',
  MT: '#dc2626', PA: '#0ea5e9', PB: '#a855f7', PE: '#22c55e', PI: '#d97706', PR: '#1d4ed8',
  RJ: '#be123c', RN: '#0f766e', RO: '#7c3aed', RR: '#65a30d', RS: '#c2410c', SC: '#0891b2',
  SE: '#a16207', SP: '#4f46e5', TO: '#db2777',
}
const FALLBACK_COLOR = '#94a3b8'

export const BiEstadosStore = createSimpleStore(
  {
    tipo: 'todos' as Tipo,
    metric: 'valorNewDriverSum' as CursoMetric,
    dateRange: [startOfCurrentMonth(), today()] as [string, string],
    loading: false as boolean,
    courses: [] as BiCourseInfo[],
    points: [] as BiEstadoPoint[],
    selectedCursoIds: [] as number[],
    error: null as string | null,
  },
  {
    setTipo: (state, tipo: Tipo) => ({ ...state, tipo }),
    setMetric: (state, metric: CursoMetric) => ({ ...state, metric }),
    setDateRange: (state, dateRange: [string, string]) => ({ ...state, dateRange }),
    setSelectedCursoIds: (state, selectedCursoIds: number[]) => ({ ...state, selectedCursoIds }),
    fetchStarted: (state) => ({ ...state, loading: true, error: null }),
    fetchSuccess: (state, payload: { courses: BiCourseInfo[]; points: BiEstadoPoint[] }) => {
      const newCourseIds = payload.courses.map((c) => c.id)
      const selectedCursoIds =
        state.selectedCursoIds.length === 0
          ? newCourseIds
          : state.selectedCursoIds.filter((id) => newCourseIds.includes(id))
      return { ...state, courses: payload.courses, points: payload.points, selectedCursoIds, loading: false }
    },
    fetchFailed: (state, error: string) => ({ ...state, loading: false, error }),
  },
  {
    thunks: {
      fetchData(tipo: Tipo, startDate: string, endDate: string) {
        return async (dispatch: any) => {
          dispatch(BiEstadosStore.actions.fetchStarted())
          try {
            const { data } = await Axios.get('/matriculas/bi/estados', {
              params: { tipo, startDate, endDate },
            })
            dispatch(BiEstadosStore.actions.fetchSuccess(data))
          } catch {
            dispatch(BiEstadosStore.actions.fetchFailed('Erro ao carregar dados'))
          }
        }
      },
    },
  }
)

export function BiEstados() {
  const init = useCallback((dispatch: any) => {
    dispatch(BiEstadosStore.thunks.fetchData('todos', startOfCurrentMonth(), today()))
  }, [])

  return (
    <BiEstadosStore.Provider init={init}>
      <Controls>
        <TipoToggle />
        <DateRangePickerControl />
        <MetricaToggle />
      </Controls>
      <AutofilterButtons />
      <CourseCheckboxList />
      <EstadosPieChart />
      <EstadosTable />
    </BiEstadosStore.Provider>
  )
}

function TipoToggle() {
  const { tipo, dateRange } = BiEstadosStore.useState()
  const dispatch = BiEstadosStore.useDispatch()

  function handleChange(newTipo: Tipo) {
    dispatch(BiEstadosStore.actions.setTipo(newTipo))
    dispatch(BiEstadosStore.thunks.fetchData(newTipo, dateRange[0], dateRange[1]))
  }

  return (
    <Radio.Group
      value={tipo}
      onChange={(e) => handleChange(e.target.value)}
      optionType="button"
      buttonStyle="solid"
      options={[
        { label: 'Todos', value: 'todos' },
        { label: 'Venda Direta', value: 'venda_direta' },
        { label: 'Parceiros', value: 'parceiros' },
      ]}
    />
  )
}

function DateRangePickerControl() {
  const { dateRange, tipo } = BiEstadosStore.useState()
  const dispatch = BiEstadosStore.useDispatch()

  function handleChange(dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) {
    if (!dates || !dates[0] || !dates[1]) return
    const start = dates[0].format('YYYY-MM-DD')
    const end = dates[1].format('YYYY-MM-DD')
    dispatch(BiEstadosStore.actions.setDateRange([start, end]))
    dispatch(BiEstadosStore.thunks.fetchData(tipo, start, end))
  }

  return (
    <RangePicker
      value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
      onChange={handleChange}
      disabledDate={(d) => d.isAfter(dayjs(), 'day')}
      format="DD/MM/YYYY"
      allowClear={false}
    />
  )
}

function MetricaToggle() {
  const { metric } = BiEstadosStore.useState()
  const dispatch = BiEstadosStore.useDispatch()

  return (
    <Radio.Group
      value={metric}
      onChange={(e) => dispatch(BiEstadosStore.actions.setMetric(e.target.value))}
      optionType="button"
      options={[
        { label: 'Receita', value: 'valorNewDriverSum' },
        { label: 'Matrículas', value: 'count' },
      ]}
    />
  )
}

function AutofilterButtons() {
  const { courses } = BiEstadosStore.useState()
  const dispatch = BiEstadosStore.useDispatch()

  function handleAutofilter(label: string) {
    const ids = AUTOFILTERS[label](courses)
    dispatch(BiEstadosStore.actions.setSelectedCursoIds(ids))
  }

  return (
    <AutofilterRow>
      {Object.keys(AUTOFILTERS).map((label) => (
        <Button key={label} size="small" onClick={() => handleAutofilter(label)}>
          {label}
        </Button>
      ))}
    </AutofilterRow>
  )
}

function CourseCheckboxList() {
  const { courses, selectedCursoIds } = BiEstadosStore.useState()
  const dispatch = BiEstadosStore.useDispatch()

  function handleToggle(cursoId: number, checked: boolean) {
    const next = checked
      ? [...selectedCursoIds, cursoId]
      : selectedCursoIds.filter((id) => id !== cursoId)
    dispatch(BiEstadosStore.actions.setSelectedCursoIds(next))
  }

  return (
    <CheckboxRow>
      {courses.map((course) => (
        <Checkbox
          key={course.id}
          checked={selectedCursoIds.includes(course.id)}
          onChange={(e) => handleToggle(course.id, e.target.checked)}
        >
          {course.nomeReduzido}
        </Checkbox>
      ))}
    </CheckboxRow>
  )
}

function EstadosPieChart() {
  const { metric } = BiEstadosStore.useState()
  const rows = useUfRows()
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!svgRef.current) return
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    if (rows.length === 0) return

    const slices = groupSmallSlices(rows)
    const radius = PIE_SIZE / 2
    const arc = d3.arc<d3.PieArcDatum<UfRow>>().innerRadius(0).outerRadius(radius - 4)
    const labelArc = d3.arc<d3.PieArcDatum<UfRow>>().innerRadius(radius * 0.62).outerRadius(radius * 0.62)
    const pie = d3.pie<UfRow>().value((row) => row.total).sort(null)
    const arcs = pie(slices)

    const g = svg.append('g').attr('transform', `translate(${radius},${radius})`)

    g.selectAll('path')
      .data(arcs)
      .join('path')
      .attr('d', arc)
      .attr('fill', (d) => UF_COLORS[d.data.uf] ?? FALLBACK_COLOR)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5)
      .on('mousemove', function (event, d) {
        if (!tooltipRef.current) return
        const [mx, my] = d3.pointer(event, svgRef.current?.parentElement)
        tooltipRef.current.style.display = 'block'
        tooltipRef.current.style.left = `${mx + 16}px`
        tooltipRef.current.style.top = `${my + 16}px`
        tooltipRef.current.innerHTML =
          `<div style="font-weight:600">${d.data.uf}</div>` +
          `<div>${formatValue(d.data.total, metric)} (${formatShare(d.data.share)})</div>`
      })
      .on('mouseleave', function () {
        if (tooltipRef.current) tooltipRef.current.style.display = 'none'
      })

    g.selectAll('text')
      .data(arcs.filter((d) => d.data.share >= MIN_LABEL_SHARE))
      .join('text')
      .attr('transform', (d) => `translate(${labelArc.centroid(d)})`)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', 600)
      .attr('fill', '#fff')
      .attr('pointer-events', 'none')
      .text((d) => d.data.uf)
  }, [rows, metric])

  return (
    <ChartContainer>
      <svg ref={svgRef} width={PIE_SIZE} height={PIE_SIZE} style={{ display: 'block' }} />
      <div ref={tooltipRef} style={tooltipStyle} />
    </ChartContainer>
  )
}

function EstadosTable() {
  const { metric } = BiEstadosStore.useState()
  const rows = useUfRows()
  const maxTotal = rows[0]?.total ?? 0

  const columns: ColumnsType<UfRow> = [
    {
      title: 'Estado',
      dataIndex: 'uf',
      render: (uf: string) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...colorDotStyle, background: UF_COLORS[uf] ?? FALLBACK_COLOR }} />
          {uf}
        </span>
      ),
    },
    {
      title: metric === 'count' ? 'Matrículas' : 'Receita NewDriver',
      dataIndex: 'total',
      render: (value: number) => {
        const barWidth = maxTotal > 0 ? (value / maxTotal) * 100 : 0
        return (
          <div style={{ position: 'relative' }}>
            <div style={{ ...barStyle, width: `${barWidth}%` }} />
            <span style={{ position: 'relative' }}>{formatValue(value, metric)}</span>
          </div>
        )
      },
    },
    {
      title: '%',
      dataIndex: 'share',
      width: 90,
      render: (share: number) => formatShare(share),
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey="uf"
      pagination={false}
      size="small"
      style={{ marginTop: 16 }}
    />
  )
}

function useUfRows(): UfRow[] {
  const { points, selectedCursoIds, metric } = BiEstadosStore.useState()

  return useMemo(() => {
    const totalsByUf = new Map<string, number>()
    for (const point of points) {
      if (!selectedCursoIds.includes(point.cursoId)) continue
      totalsByUf.set(point.uf, (totalsByUf.get(point.uf) ?? 0) + point[metric])
    }
    const grandTotal = Array.from(totalsByUf.values()).reduce((sum, value) => sum + value, 0)
    return Array.from(totalsByUf.entries())
      .filter(([, total]) => total > 0)
      .map(([uf, total]) => ({ uf, total, share: grandTotal > 0 ? total / grandTotal : 0 }))
      .sort((a, b) => b.total - a.total)
  }, [points, selectedCursoIds, metric])
}

function groupSmallSlices(rows: UfRow[]): UfRow[] {
  const bigSlices = rows.filter((row) => row.share >= MIN_SLICE_SHARE)
  const smallSlices = rows.filter((row) => row.share < MIN_SLICE_SHARE)
  if (smallSlices.length <= 1) return rows
  const outros: UfRow = {
    uf: OUTROS_LABEL,
    total: smallSlices.reduce((sum, row) => sum + row.total, 0),
    share: smallSlices.reduce((sum, row) => sum + row.share, 0),
  }
  return [...bigSlices, outros]
}

function formatValue(value: number, metric: CursoMetric): string {
  if (metric === 'valorNewDriverSum') {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }
  return String(value)
}

function formatShare(share: number): string {
  return `${(share * 100).toFixed(1).replace('.', ',')}%`
}

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const AutofilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`

const CheckboxRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`

const ChartContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  display: 'none',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: 12,
  pointerEvents: 'none',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  zIndex: 10,
}

const colorDotStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'inline-block',
}

const barStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  background: 'rgba(79,142,247,0.18)',
  borderRadius: 2,
}

function startOfCurrentMonth(): string {
  return dayjs().startOf('month').format('YYYY-MM-DD')
}

function today(): string {
  return new Date().toLocaleDateString('en-CA')
}
